import React, { useState, useEffect, useMemo } from 'react';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import SchoolDiscoveryPanel, { useDiscoveryApi } from '../components/schools/SchoolDiscoveryPanel';
import { canFindSchoolAsCoach } from '../utils/roles';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/dateFormat';


export default function ApplyPage() {
    const { t } = useTranslation(['applyPage', 'common', 'nav', 'messages', 'errors']);
    const statusConfig = useMemo(() => ({
        pending: { label: t('applyPage:statusPending'), color: '#e65100', bg: '#fff3e0' },
        approved: { label: t('applyPage:statusApproved'), color: '#2e7d32', bg: '#e8f5e9' },
        rejected: { label: t('applyPage:statusRejected'), color: '#c62828', bg: '#fce4ec' }
    }), [t]);
    const api = useDiscoveryApi();
    const [myApps, setMyApps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const role = localStorage.getItem('role') || 'Member';
    const applyAsCoach = role === 'Coach' || canFindSchoolAsCoach();

    useEffect(() => {
        loadMyApps();
    }, []);

    async function loadMyApps() {
        try {
            const res = await api.get('/application/my');
            setMyApps(res.data);
        } catch {
            setMyApps([]);
        }
    }

    async function handleApply(schoolId) {
        setLoading(true);
        try {
            await api.post('/application', {
                schoolId,
                role: applyAsCoach ? 'coach' : 'student',
                message: message.trim() || null
            });
            alert(t('applyPage:submitSuccess'));
            loadMyApps();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
        setLoading(false);
    }

    function renderApplyButton(school) {
        const alreadyApplied = myApps.some(
            a => a.schoolId === school.id && a.status === 'pending'
        );
        const approved = myApps.some(
            a => a.schoolId === school.id && a.status === 'approved'
        );

        return (
            <button
                type="button"
                onClick={() => handleApply(school.id)}
                disabled={loading || alreadyApplied || approved}
                style={{
                    padding: '10px 16px', borderRadius: 10, border: 'none',
                    cursor: alreadyApplied || approved ? 'default' : 'pointer',
                    fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                    background: approved ? '#e8f5e9' : alreadyApplied ? '#f5f5f5' : '#EB3333',
                    color: approved ? '#2e7d32' : alreadyApplied ? '#888' : '#fff'
                }}
            >
                {approved ? t('applyPage:btnApproved') : alreadyApplied ? t('applyPage:btnAlreadyApplied') : t('applyPage:btnApply')}
            </button>
        );
    }

    const myAppsBlock = myApps.length > 0 ? (
        <div style={{
            background: 'white', borderRadius: '14px',
            padding: '20px', marginBottom: '24px', border: '1px solid var(--sp-gray-100)'
        }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '16px' }}>{t('applyPage:myApplications')}</h3>
            {myApps.map(app => {
                const conf = statusConfig[app.status] || statusConfig.pending;
                return (
                    <div key={app.id} style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', padding: '10px 0',
                        borderBottom: '1px solid #f5f5f5'
                    }}>
                        <div>
                            <div style={{ fontWeight: '600' }}>{app.schoolName}</div>
                            <div style={{ fontSize: '13px', color: '#888' }}>
                                {formatDate(app.createdAt)}
                                {app.response && ` · ${app.response}`}
                            </div>
                        </div>
                        <span style={{
                            background: conf.bg, color: conf.color,
                            padding: '4px 12px', borderRadius: '12px',
                            fontSize: '13px', fontWeight: '600'
                        }}>
                            {conf.label}
                        </span>
                    </div>
                );
            })}
        </div>
    ) : null;

    return (
        <OwnerPageLayout
            title={t('applyPage:title')}
            subtitle={applyAsCoach ? t('applyPage:subtitleCoach') : t('applyPage:subtitleStudent')}
            maxWidth={960}
        >
                <div style={{ marginBottom: 16 }}>
                    <input
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder={t('applyPage:messagePlaceholder')}
                        style={{
                            width: '100%', padding: '11px 14px', borderRadius: '10px',
                            border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
                            outline: 'none', boxSizing: 'border-box'
                        }}
                    />
                </div>

                <SchoolDiscoveryPanel
                    api={api}
                    onApply={() => {}}
                    renderActionButton={renderApplyButton}
                    showMyApps={myAppsBlock}
                />
        </OwnerPageLayout>
    );
}
